import { Card, CardContent } from '@sitewise/ui';
import { type ReactNode } from 'react';


import { useBrandingStore } from '../../stores/branding-store.js';

interface Props {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Shared chrome for every un-authed page. Branded with the org's logo +
 * name when available, falling back to the SiteWise wordmark on the
 * super-admin / unknown surfaces (per DESIGN_SYSTEM §15: SiteWise's brand
 * never shows on org chrome).
 */
export function AuthShell({ title, description, children, footer }: Props) {
  const branding = useBrandingStore((s) => s.snapshot);
  const showWordmark = !branding;

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center gap-6 bg-bg px-6 py-12">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden />
      <header className="flex flex-col items-center gap-3">
        {branding?.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt={`${branding.orgName} logo`}
            className="h-10 w-auto"
          />
        ) : showWordmark ? (
          <div className="rounded-[10px] bg-sidebar px-3 py-1.5 text-sm font-bold tracking-tight text-sidebar-fg">
            SiteWise
          </div>
        ) : null}
        <div className="text-center">
          {branding ? (
            <p className="text-sm font-medium text-fg-secondary">{branding.orgName}</p>
          ) : null}
        </div>
      </header>

      <Card className="w-full max-w-sm">
        <CardContent className="space-y-5 pt-6">
          <div className="space-y-1.5">
            <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
            {description ? (
              <p className="text-sm text-fg-secondary">{description}</p>
            ) : null}
          </div>
          {children}
        </CardContent>
      </Card>

      {footer ? <div className="text-sm text-fg-secondary">{footer}</div> : null}
    </div>
  );
}
